import { collection, query, where, getDocs, writeBatch, Timestamp, addDoc, getDoc, doc, updateDoc, onSnapshot, arrayUnion, deleteDoc } from 'firebase/firestore';
import { db } from "../firebase-config";
import { translateReviewCycle } from '../utils/translations/ReviewCycleUtils';

export const createPerformanceReview = async (
  organizationId,
  employeeId,
  initiatedBy,
  reviewCycle,
  questions,
  dueDate,
  title
) => {
  try {
    // Reference to the employee document
    const employeeRef = doc(
      db, 
      `organizations/${organizationId}/employees/${employeeId}`
    );

    // Reference for the new performance review (with pre-generated ID)
    const performanceReviewRef = doc(
      collection(
        db, 
        `organizations/${organizationId}/employees/${employeeId}/performance_reviews`
      )
    );
    
    // Reference for the organization-level performance review (using same ID)
    const orgPerformanceReviewRef = doc(
      db,
      `organizations/${organizationId}/performance_reviews/${performanceReviewRef.id}`
    );

    const timestamp = Timestamp.now()
    const dueDateTimestamp = Timestamp.fromDate(new Date(dueDate));

    // Prepare the review data
    const reviewData = {
      id: performanceReviewRef.id,
      initiated_by: initiatedBy,
      employee_id: employeeId,
      review_cycle: reviewCycle,
      title: title,
      
      // Initialize status for both employee and manager
      status: {
        employee: 'pending',
        manager: 'pending'
      },
      
      // Initialize acknowledgments
      acknowledgments: {
        employee_acknowledged: false,
        manager_acknowledged: false
      },
      
      // Track submission status
      employee_reviewed: false,
      manager_reviewed: false,
      employee_submitted_at: null,
      manager_submitted_at: null,
      
      // Editability
      editable_by: 'employee', // Initially, employee can edit
      
      // Timestamps
      created_at: timestamp,
      updated_at: timestamp,
      due_date: dueDateTimestamp,
      
      // Finalization flag
      finalized: false,
      
      // Meeting notes (optional)
      meeting_notes: null,
      
      // Prepare questions with empty responses
      questions: questions.map(question => ({
        ...question,
        responses: {
          employee: {
            answer: '',
            comments: '',
            timestamp: null
          },
          manager: {
            answer: '',
            comments: '',
            timestamp: null
          }
        }
      })),
      
      // Feedback placeholders
      feedback: {
        employee_feedback: null,
        manager_feedback: null
      },
      
      // Optional template reference
      template_id: ''
    };

    // Minimal data for organization-level performance review
    const orgReviewData = {
      review_id: performanceReviewRef.id, // Reference to the review
      employee_id: employeeId,
      initiated_by: initiatedBy,
      review_cycle: reviewCycle,
      title: title,
      due_date: dueDateTimestamp,
      created_at: timestamp,
      status: {
        employee: 'pending',
        manager: 'pending'
      }
    };

    // Batch write to update employee, performance review, and org-level performance review
    const batch = writeBatch(db);

    // Update performance review in employee's subcollection
    batch.set(performanceReviewRef, reviewData);

    // Update organization-level performance review with minimal data
    batch.set(orgPerformanceReviewRef, orgReviewData);

    // Update employee document to track performance reviews
    batch.update(employeeRef, {
      performance_reviews: arrayUnion(performanceReviewRef.id)
    });

    // Commit the batch
    await batch.commit();

    return { 
      success: true, 
      reviewId: performanceReviewRef.id,
      message: 'Performance review created successfully' 
    };
  } catch (error) {
    console.error("Error creating performance review:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to create performance review' 
    };
  }
};


// Query reviews for a manager (across all their direct reports)
export const getManagerReviews = async (organizationId, managerId) => {
  const reviews = [];
  const employeesRef = collection(db, `organizations/${organizationId}/employees`);
  const employeesSnapshot = await getDocs(
    query(employeesRef, where('manager_id', '==', managerId))
  );

  for (const employeeDoc of employeesSnapshot.docs) {
    const reviewsRef = collection(employeeDoc.ref, 'performance_reviews');
    const reviewsSnapshot = await getDocs(reviewsRef);
    
    reviewsSnapshot.forEach(review => {
      reviews.push({
        id: review.id,
        employeeId: employeeDoc.id,
        employeeName: employeeDoc.data().first_name + ' ' + employeeDoc.data().last_name,
        ...review.data()
      });
    });
  }

  return reviews;
};

export const getEmployeePerformanceReview = async (organizationId, employeeId, reviewId) => {

  try {
    const reviewRef = doc(
      db, 
      `organizations/${organizationId}/employees/${employeeId}/performance_reviews/${reviewId}`
    );

    const reviewSnapshot = await getDoc(reviewRef);

    if (!reviewSnapshot.exists()) {
      return { 
        success: false, 
        error: 'Performance review not found' 
      };
    }

    return { 
      success: true, 
      data: {
        id: reviewSnapshot.id,
        ...reviewSnapshot.data()
      }
    };
  } catch (error) {
    console.error("Error fetching performance review:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to fetch performance review' 
    };
  }
};

// Get reviews for a specific employee
export const getEmployeeReviews = async (organizationId, employeeId) => {
  const reviewsRef = collection(db, 
    `organizations/${organizationId}/employees/${employeeId}/performance_reviews`);
  const snapshot = await getDocs(reviewsRef);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update review status and feedback
export const updateReviewFeedback = async ({
  organizationId,
  employeeId,
  reviewId,
  feedback
}) => {
  try {
    // Get references to both the employee-level and org-level review documents
    const reviewRef = doc(
      db, 
      `organizations/${organizationId}/employees/${employeeId}/performance_reviews/${reviewId}`
    );
    
    const orgReviewRef = doc(
      db,
      `organizations/${organizationId}/performance_reviews/${reviewId}`
    );

    const timestamp = Timestamp.now();

    // Create batch write
    const batch = writeBatch(db);

    // Update the employee-level review
    batch.update(reviewRef, {
      ...feedback,
      updated_at: timestamp
    });

    // Update the org-level review if both parties have acknowledged
    if (feedback.acknowledgments?.employee_acknowledged && 
        feedback.acknowledgments?.manager_acknowledged) {
      batch.update(orgReviewRef, {
        status: {
          employee: 'ACKNOWLEDGED',
          manager: 'ACKNOWLEDGED'
        },
        updated_at: timestamp
      });
    }

    await batch.commit();

    return { 
      success: true, 
      message: 'Review acknowledgment updated successfully' 
    };
  } catch (error) {
    console.error("Error updating review acknowledgment:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to update review acknowledgment' 
    };
  }
};

export const addPerformanceReviewTemplate = async (
  organizationId,
  employeeId,
  template
) => {
  try {
    const performanceTemplateRef = collection(db, "organizations", organizationId, "performance_review_templates");
    const docRef = await addDoc(performanceTemplateRef, {
        ...template,
        created_by: employeeId,
        created_on: new Date(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
      console.error("Error adding category:", error);
      return { success: false, error };
  }
}

export const performanceReviewSnapshotListener = (organization, onPerformanceReviewsUpdate) => {

  const templateQuery = query(collection(db, "organizations", organization, "performance_reviews"))
  const unsubscribe = onSnapshot(templateQuery, (querySnapshot) => {
      const reviews = [];
      querySnapshot.forEach((doc) => {
        reviews.push({ id: doc.id, ...doc.data() });
      });
      onPerformanceReviewsUpdate(reviews);
  })
  return () => unsubscribe()
}

export const performanceReviewTemplateSnapshotListener = (organization, templateId, onTemplateUpdate) => {
  const assetDocRef = doc(db, "organizations", organization, "performance_review_templates", templateId);

  const unsubscribe = onSnapshot(assetDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
          onTemplateUpdate({ id: docSnapshot.id, ...docSnapshot.data() });
      } else {
          onTemplateUpdate(null);
      }
  });
  return unsubscribe;
};

export const performanceReviewTemplatesSnapshotListener = (organization, onTemplateUpdate) => {

  const templateQuery = query(collection(db, "organizations", organization, "performance_review_templates"))
  const unsubscribe = onSnapshot(templateQuery, (querySnapshot) => {
      const templates = [];
      querySnapshot.forEach((doc) => {
        templates.push({ id: doc.id, ...doc.data() });
      });
      onTemplateUpdate(templates);
  })
  return () => unsubscribe()
}

export const getOrganizationPerformanceReview = async (organizationId, reviewId) => {
  const reviewRef = doc(db, "organizations", organizationId, "performance_reviews", reviewId);
  const reviewSnapshot = await getDoc(reviewRef);
  return reviewSnapshot.data();
}

export const getPerformanceReview = async (organization, templateId) => {
  try {
    // Reference to the specific performance review template document
    const docRef = doc(
      db,
      "organizations",
      organization,
      "performance_review_templates",
      templateId
    );
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, error: "Template not found" };
    }
  } catch (error) {
    console.error("Error fetching performance review template:", error);
    return { success: false, error: error.message || "An unexpected error occurred" };
  }
};

// Update performance review responses
export const updatePerformanceReviewResponses = async ({
  organizationId,
  employeeId,
  reviewId,
  responses,
  isManager
}) => {
  try {
    const reviewRef = doc(
      db, 
      `organizations/${organizationId}/employees/${employeeId}/performance_reviews/${reviewId}`
    );

    // Fetch current document to get existing data
    const reviewSnapshot = await getDoc(reviewRef);
    
    // Add explicit error handling
    if (!reviewSnapshot.exists()) {
      console.error('No review found with the given parameters');
      return { 
        success: false, 
        error: 'Performance review not found' 
      };
    }

    const currentData = reviewSnapshot.data();

    // Add additional validation
    if (!currentData) {
      console.error('Review data is undefined');
      return { 
        success: false, 
        error: 'Invalid review data' 
      };
    }

    if (!currentData.questions || !Array.isArray(currentData.questions)) {
      console.error('Questions array is missing or invalid', currentData);
      return { 
        success: false, 
        error: 'Invalid review questions structure' 
      };
    }

    const timestamp = Timestamp.now()

    // Create a copy of questions to modify
    const updatedQuestions = currentData.questions.map(question => {
      const responseForQuestion = responses[question.id];
      
      if (responseForQuestion) {
        question.responses[isManager ? 'manager' : 'employee'] = {
          answer: responseForQuestion.rating || '',
          comments: responseForQuestion.comments || '',
          timestamp: timestamp
        };
      }
      
      return question;
    });

    // Prepare update data
    const updateData = {
        [`status.${isManager ? 'manager' : 'employee'}`]: 'completed',
        [`${isManager ? 'manager' : 'employee'}_reviewed`]: true,
        [`${isManager ? 'manager' : 'employee'}_submitted_at`]: timestamp,
        questions: updatedQuestions,
        updated_at: timestamp,
        editable_by: isManager ? 'employee' : 'manager'
    };

    // Determine if review is fully completed
    if (currentData.status.employee === 'completed' && 
        currentData.status.manager === 'completed') {
        updateData.editable_by = null; // No one can edit when both have submitted
    }

    // Update the organization-level performance review as well
    const orgReviewRef = doc(
      db,
      `organizations/${organizationId}/performance_reviews/${reviewId}`
    );

    // Batch write to update both documents
    const batch = writeBatch(db);
    
    batch.update(reviewRef, updateData);
    
    // Update only the status in the organization-level review
    batch.update(orgReviewRef, {
      [`status.${isManager ? 'manager' : 'employee'}`]: 'completed',
      updated_at: timestamp
    });

    await batch.commit();

    return { 
      success: true, 
      message: 'Performance review updated successfully' 
    };
  } catch (error) {
    console.error("Error updating performance review:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to update performance review' 
    };
  }
};

export const deletePerformanceReviewTemplate = async (organizationId, templateId) => {
    try {
        const templateRef = doc(
            db,
            "organizations",
            organizationId,
            "performance_review_templates",
            templateId
        );

        await deleteDoc(templateRef);
        
        return { 
            success: true, 
            message: 'Template deleted successfully' 
        };
    } catch (error) {
        console.error("Error deleting performance review template:", error);
        return { 
            success: false, 
            error: error.message || 'Failed to delete template' 
        };
    }
};

export const updatePerformanceReviewTemplate = async (organizationId, templateId, updatedTemplate) => {
    try {
        const templateRef = doc(
            db,
            "organizations",
            organizationId,
            "performance_review_templates",
            templateId
        );

        await updateDoc(templateRef, {
            ...updatedTemplate,
            updated_at: new Date()
        });
        
        return { 
            success: true, 
            message: 'Template updated successfully' 
        };
    } catch (error) {
        console.error("Error updating performance review template:", error);
        return { 
            success: false, 
            error: error.message || 'Failed to update template' 
        };
    }
};

// New function specifically for acknowledgments
export const updateReviewAcknowledgment = async ({
  organizationId,
  employeeId,
  reviewId,
  isManager
}) => {
  try {
    const reviewRef = doc(
      db, 
      `organizations/${organizationId}/employees/${employeeId}/performance_reviews/${reviewId}`
    );

    // Get current review data to check existing acknowledgments
    const reviewSnapshot = await getDoc(reviewRef);
    const currentData = reviewSnapshot.data();
    
    // Update acknowledgments while preserving existing acknowledgment
    const updatedAcknowledgments = {
      ...currentData.acknowledgments,
      [isManager ? 'manager_acknowledged' : 'employee_acknowledged']: true
    };

    await updateDoc(reviewRef, {
      acknowledgments: updatedAcknowledgments,
      updated_at: Timestamp.now()
    });

    return { 
      success: true,
      acknowledgments: updatedAcknowledgments
    };
  } catch (error) {
    console.error("Error updating review acknowledgment:", error);
    return { 
      success: false, 
      error: error.message || 'Failed to update acknowledgment'
    };
  }
};
